import { z } from 'zod';

/**
 * Case Wiki — penal V1 participant classification + procedural status history.
 *
 * Shared source of truth for:
 * - `case_parties.categorie_intervenant`
 * - `case_parties.role_partie_proces`
 * - `case_parties.fonction_acteur_procedure`
 * - `case_party_procedural_statuses`
 *
 * Runtime contract: ingest can create source-bound status observations through
 * `record_party_procedural_status`; lint/review intentionally cannot. The
 * bundle exposes the append-only history so UI can derive current status.
 */

export const CATEGORIE_INTERVENANT_VALUES = [
  'partie_proces',
  'acteur_procedure',
  'representant_legal',
  'personne_citee',
] as const;
export const CategorieIntervenantSchema = z.enum(CATEGORIE_INTERVENANT_VALUES);
export type CategorieIntervenant = z.infer<typeof CategorieIntervenantSchema>;

export const ROLE_PARTIE_PROCES_VALUES = [
  'ministere_public',
  'accuse',
  'prevenu',
  'partie_civile',
] as const;
export const RolePartieProcesSchema = z.enum(ROLE_PARTIE_PROCES_VALUES);
export type RolePartieProces = z.infer<typeof RolePartieProcesSchema>;

export const FONCTION_ACTEUR_PROCEDURE_VALUES = [
  'procureur_republique',
  'procureur_general',
  'substitut_procureur',
  'juge_instruction',
  'jld',
  'opj',
  'apj',
  'greffier',
  'expert',
  'interprete',
] as const;
export const FonctionActeurProcedureSchema = z.enum(FONCTION_ACTEUR_PROCEDURE_VALUES);
export type FonctionActeurProcedure = z.infer<typeof FonctionActeurProcedureSchema>;

export const OBSERVATION_KIND_VALUES = ['statut', 'mesure_liberte', 'issue'] as const;
export const ProceduralObservationKindSchema = z.enum(OBSERVATION_KIND_VALUES);
export type ProceduralObservationKind = z.infer<typeof ProceduralObservationKindSchema>;

export const STATUT_PROCEDURAL_VALUES = [
  'suspect',
  'mis_en_cause',
  'garde_a_vue',
  'temoin',
  'temoin_assiste',
  'mis_en_examen',
  'prevenu',
  'accuse',
  'partie_civile',
  'victime',
] as const;
export const StatutProceduralSchema = z.enum(STATUT_PROCEDURAL_VALUES);
export type StatutProcedural = z.infer<typeof StatutProceduralSchema>;

export const MESURE_LIBERTE_VALUES = [
  'remis_en_liberte',
  'controle_judiciaire',
  'assignation_residence',
  'detention_provisoire',
  'liberte',
] as const;
export const MesureLiberteSchema = z.enum(MESURE_LIBERTE_VALUES);
export type MesureLiberte = z.infer<typeof MesureLiberteSchema>;

export const PROCEDURAL_OUTCOME_VALUES = [
  'non_lieu',
  'relaxe',
  'acquitte',
  'condamne',
] as const;
export const ProceduralOutcomeSchema = z.enum(PROCEDURAL_OUTCOME_VALUES);
export type ProceduralOutcome = z.infer<typeof ProceduralOutcomeSchema>;

export const PROCEDURAL_STATUS_VALUES = [
  ...STATUT_PROCEDURAL_VALUES,
  ...MESURE_LIBERTE_VALUES,
  ...PROCEDURAL_OUTCOME_VALUES,
] as const;
export const ProceduralStatusSchema = z.enum(PROCEDURAL_STATUS_VALUES);
export type ProceduralStatus = z.infer<typeof ProceduralStatusSchema>;

export const PROCEDURAL_STATUS_CONFIDENCE_VALUES = [
  'high',
  'medium',
  'low',
] as const;
export const ProceduralStatusConfidenceSchema = z.enum(
  PROCEDURAL_STATUS_CONFIDENCE_VALUES,
);
export type ProceduralStatusConfidence = z.infer<
  typeof ProceduralStatusConfidenceSchema
>;

export const PROCEDURAL_STATUS_VALUES_BY_KIND = {
  statut: STATUT_PROCEDURAL_VALUES,
  mesure_liberte: MESURE_LIBERTE_VALUES,
  issue: PROCEDURAL_OUTCOME_VALUES,
} as const satisfies Record<ProceduralObservationKind, readonly ProceduralStatus[]>;

export function isProceduralStatusAllowedForObservationKind(
  observationKind: ProceduralObservationKind,
  status: ProceduralStatus,
): boolean {
  const allowedValues = PROCEDURAL_STATUS_VALUES_BY_KIND[observationKind];
  return (allowedValues as readonly string[]).includes(status);
}

export const ProceduralStatusObservationSchema = z
  .object({
    observation_kind: ProceduralObservationKindSchema,
    status: ProceduralStatusSchema,
  })
  .superRefine((value, ctx) => {
    if (
      !isProceduralStatusAllowedForObservationKind(
        value.observation_kind,
        value.status,
      )
    ) {
      const allowed = PROCEDURAL_STATUS_VALUES_BY_KIND[value.observation_kind].join(
        ', ',
      );
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['status'],
        message: `status "${value.status}" is invalid for observation_kind "${value.observation_kind}" (allowed: ${allowed})`,
      });
    }
  });
export type ProceduralStatusObservation = z.infer<
  typeof ProceduralStatusObservationSchema
>;
